// Tiny command bus that lets the global voice dock drive whatever editor is
// currently mounted, without prop-drilling. Editors register named handlers on
// mount (apply_look, adjust, …); the voice agent's directives are dispatched
// here. Also carries a small live "context" blob (movie/clips/presets) that the
// dock injects into the agent's system prompt at connect time.
import { useEffect, useRef } from 'react';

const handlers = new Map(); // name -> fn(args) => result
let context = {}; // { pageName, movieName, clips, presets, gradeSummary }
const ctxListeners = new Set();

export const agentBus = {
  register(name, fn) {
    handlers.set(name, fn);
    return () => { if (handlers.get(name) === fn) handlers.delete(name); };
  },
  has(name) { return handlers.has(name); },
  async call(name, args) {
    const fn = handlers.get(name);
    if (!fn) return { ok: false, error: `no_handler:${name}` };
    try { return await fn(args || {}); } catch (e) { return { ok: false, error: e.message }; }
  },
  setContext(patch) {
    context = { ...context, ...patch };
    ctxListeners.forEach((l) => l(context));
  },
  getContext() { return context; },
  onContext(fn) { ctxListeners.add(fn); return () => ctxListeners.delete(fn); },
};

// Dispatch a directive from the agent (client tool). navigate/switch_tab and all
// editor actions resolve to registered handlers.
export async function executeDirective(name, args) {
  return agentBus.call(name, args || {});
}

// Register a set of named handlers for the lifetime of a component. The handler
// functions can change every render (we always call the latest via a ref); the
// set of NAMES is fixed at mount.
export function useAgentHandlers(handlersObj) {
  const ref = useRef(handlersObj);
  ref.current = handlersObj;
  useEffect(() => {
    const names = Object.keys(ref.current || {});
    const unregs = names.map((name) => agentBus.register(name, (args) => ref.current[name] && ref.current[name](args)));
    return () => unregs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// Publish live context for the agent prompt; cleared on unmount.
export function useAgentContext(patch, deps) {
  useEffect(() => {
    agentBus.setContext(patch || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps || []);
}
